/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import * as S from "./styled"

import Header from "../../components/Header"
import Footer from "../../components/Footer"
import Container from "../../components/Container"
import ApprovedPanel from "../../components/ApprovedPanel"

import { QRCodeSVG } from "qrcode.react"
import { Check, Copy, Download, Share2, Sun, Ticket } from "lucide-react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import loadingAnimation from "../../assets/animations/loading"

import getStore from "../../store"
import { Api } from "../../api"
import downloadTickets from "../../utils/pdf"
import { TEventData } from "../../utils/@types/data/event"
import { TShoppingTicket } from "../../utils/@types/data/ticket"
import {
  findEventsBySlug,
  ORDER_TOKEN_PARAM,
  resolvePublicAccess,
} from "../../utils/tb/ticketsLink"
import { parsePurchaseTickets } from "../../utils/tb/parsePurchaseTickets"
import { consumeLinkToken } from "../../utils/tb/publicLinkToken"

type Stage =
  | "loading" // resolvendo o evento pelo slug
  | "working" // buscando o pedido / montando os ingressos
  | "view" // ingressos carregados e exibidos na tela
  | "cancelled" // pedido cancelado
  | "denied" // pedido não encontrado
  | "login" // sem token no link e sem sessão: pede o login
  | "not-found" // slug sem evento correspondente

/**
 * Página pública dos ingressos (link enviado no "Guardar no meu WhatsApp"):
 *   /:slug/ingressos/:orderId?k=<token do pedido>
 *
 * O token do link só abre este pedido e vai só nas requisições daqui (não
 * vira sessão). Sem token, usa a sessão de quem está logado; sem sessão (ou
 * link antigo com ?t=), pede o login.
 */
const PublicTickets = () => {
  const { slug = "", orderId = "" } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Lido uma vez. O boot (src/index.tsx) já tirou o `k` da barra de endereço
  // e guardou; o parâmetro da rota fica de reserva (montagem direta, testes).
  const [linkToken] = useState(
    () => consumeLinkToken() || searchParams.get(ORDER_TOKEN_PARAM),
  )
  const { controllers } = getStore()

  const [stage, setStage] = useState<Stage>("loading")
  const [tickets, setTickets] = useState<TShoppingTicket[]>([])
  const [downloadStatus, setDownloadStatus] = useState<
    "done" | "failed" | null
  >(null)
  const [busy, setBusy] = useState<"download" | "share" | null>(null)
  const [shareError, setShareError] = useState("")
  const [copied, setCopied] = useState<number | null>(null)
  // Ingresso em foco no carrossel horizontal.
  const [active, setActive] = useState(0)

  const carouselRef = useRef<HTMLUListElement | null>(null)
  const eventRef = useRef<TEventData | null>(null)
  const ticketsRef = useRef<TShoppingTicket[]>([])
  // PDF gerado em segundo plano: compartilhar precisa acontecer logo no gesto
  // do usuário (iOS/Safari recusa se a geração demorar antes do share).
  const pdfFileRef = useRef<File | null>(null)

  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({
      files: [new File([""], "ingressos.pdf", { type: "application/pdf" })],
    })

  const loadOrder = async (
    candidates: any[],
    token: string | undefined,
    isCancelled: () => boolean,
  ): Promise<boolean> => {
    for (const candidate of candidates) {
      const [info, purchase] = await Promise.all([
        Api.get.eventInfo({ eventId: candidate.id }),
        Api.get.purchaseInfo({ eventId: candidate.id, orderId, token }),
      ])

      if (isCancelled()) return true

      if (!info.ok || !purchase.ok || !purchase.data.products?.length) continue

      // Status do pedido (cancelado ou válido), como em MyTickets.
      const statusRes = await Api.get.ticketStatus({
        eventId: candidate.id,
        qrLabel: purchase.data.products[0].qr_label,
        token,
      })

      if (isCancelled()) return true

      if (!statusRes.ok) continue

      if (statusRes.data.status === "cancelamento") {
        setStage("cancelled")
        return true
      }

      if (statusRes.data.status !== "validado") continue

      eventRef.current = info.data
      ticketsRef.current = parsePurchaseTickets(
        purchase.data,
        info.data,
        orderId,
      )

      // Contexto para o "Comprar mais" (Home depende do evento na sessão).
      sessionStorage.setItem("event", JSON.stringify(info.data))
      controllers.event.setData(info.data)

      setTickets(ticketsRef.current)
      setStage("working")

      const file = await getPdfFile()

      if (isCancelled()) return true

      if (file) {
        // Abre o PDF na mesma aba para pré-visualização. `replace` evita que o
        // "voltar" reabra esta página e repita o fluxo.
        window.location.replace(URL.createObjectURL(file))
        return true
      }

      // Falha ao gerar o PDF: mantém a visualização dos ingressos na tela.
      setStage("view")

      return true
    }

    return false
  }

  // Contexto do evento para o login e o "Meus ingressos" depois dele.
  const rememberEvent = async (candidate: any) => {
    const info = await Api.get.eventInfo({ eventId: candidate.id })
    if (!info.ok) return
    sessionStorage.setItem("event", JSON.stringify(info.data))
    controllers.event.setData(info.data)
  }

  // Segurança: trackers por evento (Google Analytics / Meta Pixel) leem a URL
  // da página. O boot já tira o token; aqui só sobra algo em montagem direta.
  useEffect(() => {
    const url = new URL(window.location.href)

    if (!url.searchParams.has(ORDER_TOKEN_PARAM)) return

    url.searchParams.delete(ORDER_TOKEN_PARAM)
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    )
  }, [])

  // Resolve o evento pelo slug e carrega o pedido direto, sem confirmação.
  useEffect(() => {
    let cancelled = false

    const resolveEvent = async () => {
      const access = resolvePublicAccess({
        linkToken,
        orderId,
        hasSession: Boolean(localStorage.getItem("token")),
      })

      if (access.kind === "denied") {
        setStage("denied")
        return
      }

      try {
        const res = await Api.get.events({})

        if (cancelled) return

        const matches = res.ok
          ? findEventsBySlug(res.data.events as any[], slug)
          : []

        if (matches.length === 0) {
          setStage("not-found")
          return
        }

        if (access.kind === "login") {
          await rememberEvent(matches[0])
          if (!cancelled) setStage("login")
          return
        }

        // Token do link: só o evento do token (vários eventos podem ter o mesmo slug).
        const candidates =
          access.kind === "token"
            ? matches.filter((m: any) => String(m.id) === access.eventId)
            : matches

        const loaded = await loadOrder(
          candidates,
          access.kind === "token" ? access.token : undefined,
          () => cancelled,
        )

        if (cancelled) return

        if (!loaded) setStage("denied")
      } catch {
        if (!cancelled) setStage("not-found")
      }
    }

    resolveEvent()

    return () => {
      cancelled = true
    }
  }, [slug, orderId, linkToken])

  useEffect(() => {
    if (stage !== "view") return

    const el = carouselRef.current

    if (!el) return

    let frame = 0

    const update = () => {
      frame = 0

      const [first, second] = Array.from(el.children) as HTMLElement[]

      if (!first || !second) return

      const step = second.offsetLeft - first.offsetLeft

      setActive(Math.round(el.scrollLeft / step))
    }

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    el.addEventListener("scroll", schedule, { passive: true })

    return () => {
      if (frame) cancelAnimationFrame(frame)
      el.removeEventListener("scroll", schedule)
    }
  }, [stage, tickets.length])

  const saveFile = (file: File) => {
    const url = URL.createObjectURL(file)
    const a = document.createElement("a")

    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    a.remove()

    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  const getPdfFile = async (): Promise<File | null> => {
    if (pdfFileRef.current) return pdfFileRef.current

    const event = eventRef.current

    if (!event || ticketsRef.current.length === 0) return null

    try {
      const file = await downloadTickets(event, ticketsRef.current, false)

      if (file instanceof File) {
        pdfFileRef.current = file
        return file
      }
    } catch {}

    return null
  }

  const handleDownload = async () => {
    if (busy) return

    setBusy("download")

    const file = await getPdfFile()

    if (file) saveFile(file)

    setDownloadStatus(file ? "done" : "failed")
    setBusy(null)
  }

  const handleShare = async () => {
    if (busy) return

    setBusy("share")
    setShareError("")

    const file = await getPdfFile()

    if (!file) {
      setShareError("Não foi possível preparar o PDF. Tente novamente.")
    } else {
      try {
        await navigator.share({
          files: [file],
          title: `Meus ingressos - ${eventRef.current?.name ?? ""}`.trim(),
        })
      } catch (error: any) {
        // Cancelar a folha de compartilhamento não é erro.
        if (error?.name !== "AbortError")
          setShareError("Não foi possível compartilhar. Tente baixar o PDF.")
      }
    }

    setBusy(null)
  }

  const copyCode = async (code: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(idx)
      setTimeout(() => setCopied(null), 2000)
    } catch {}
  }

  const keepShopping = () => {
    navigate("/", { replace: true, state: { backRoute: "/eventSelect" } })
  }

  const eventName = eventRef.current?.name

  return (
    <S.Page>
      <Header />

      <Container fullHeight={true}>
        <S.Main>
          {stage === "view" && (
            <S.View $k={2}>
              <S.ViewHeader>
                <h1>
                  <Ticket aria-hidden="true" />
                  {tickets.length === 1
                    ? "Seu ingresso"
                    : `Seus ${tickets.length} ingressos`}
                </h1>
                {eventName && <strong>{eventName}</strong>}
              </S.ViewHeader>

              {downloadStatus ? (
                <ApprovedPanel
                  variant="download"
                  downloadStatus={downloadStatus}
                  eventName={eventName}
                  ticketsList={[]}
                  onDownload={handleDownload}
                  onKeepShopping={keepShopping}
                />
              ) : (
                <S.Actions>
                  <S.ActionButton
                    type="button"
                    $primary
                    disabled={busy !== null}
                    onClick={handleDownload}
                  >
                    <Download aria-hidden="true" />
                    <span>
                      <strong>
                        {busy === "download" ? "Gerando PDF..." : "Baixar PDF"}
                      </strong>
                      <small>Salvar no celular</small>
                    </span>
                  </S.ActionButton>

                  {canShareFiles && (
                    <S.ActionButton
                      type="button"
                      disabled={busy !== null}
                      onClick={handleShare}
                    >
                      <Share2 aria-hidden="true" />
                      <span>
                        <strong>
                          {busy === "share" ? "Preparando..." : "Compartilhar"}
                        </strong>
                        <small>Enviar p/ WhatsApp</small>
                      </span>
                    </S.ActionButton>
                  )}

                  {shareError && (
                    <S.ActionError role="alert">{shareError}</S.ActionError>
                  )}
                </S.Actions>
              )}

              <S.Carousel ref={carouselRef} aria-label="Seus ingressos">
                {tickets.map((ticket, idx) => (
                  <S.Slide
                    key={`${ticket.qr_label}-${idx}`}
                    $active={idx === active}
                  >
                    <S.TicketCard>
                      <div className="top">
                        <div className="head">
                          <span className="index">
                            Ingresso {idx + 1} de {tickets.length}
                          </span>
                          {tickets.length > 1 && tickets.length <= 6 && (
                            <span className="dots" aria-hidden="true">
                              {tickets.map((_, d) => (
                                <i key={d} className={d === idx ? "on" : ""} />
                              ))}
                            </span>
                          )}
                        </div>
                        <strong>{ticket.name}</strong>
                        {!ticket.is_mesa && ticket.batch_name && (
                          <span className="batch">{ticket.batch_name}</span>
                        )}
                      </div>
                      <div className="bottom">
                        <div className="qr">
                          <QRCodeSVG
                            value={String(ticket.qr_data)}
                            size={190}
                            aria-label={`QR Code do ingresso ${idx + 1}`}
                          />
                        </div>
                        {ticket.qr_label && (
                          <button
                            type="button"
                            className="code"
                            onClick={() => copyCode(ticket.qr_label, idx)}
                            aria-label="Copiar código do ingresso"
                          >
                            Código: {ticket.qr_label}
                            {copied === idx ? (
                              <Check aria-hidden="true" />
                            ) : (
                              <Copy aria-hidden="true" />
                            )}
                          </button>
                        )}
                        <div className="hint">
                          <Sun aria-hidden="true" />
                          <span>Aumente o brilho do celular para leitura</span>
                        </div>
                      </div>
                    </S.TicketCard>
                  </S.Slide>
                ))}
              </S.Carousel>

              <S.LinkButton type="button" onClick={keepShopping}>
                Comprar mais
              </S.LinkButton>
            </S.View>
          )}

          {stage !== "view" && (
            <S.Block $k={2}>
              {(stage === "loading" || stage === "working") && (
                <>
                  <div style={{ width: 160 }}>
                    <DotLottieReact
                      data={loadingAnimation}
                      loop
                      autoplay
                      width="100%"
                    />
                  </div>
                  <h2>
                    {stage === "loading"
                      ? "Carregando..."
                      : "Gerando o PDF dos seus ingressos..."}
                  </h2>
                </>
              )}

              {stage === "cancelled" && (
                <>
                  <h2>Compra cancelada</h2>
                  <p>
                    Esta compra foi cancelada e seus ingressos não estão mais
                    válidos.
                  </p>
                  <S.Button type="button" onClick={keepShopping}>
                    Ir para o início
                  </S.Button>
                </>
              )}

              {stage === "denied" && (
                <>
                  <h2>Não foi possível abrir seus ingressos</h2>
                  <p>
                    Não encontramos esse pedido. Confira o link recebido ou
                    acesse nossa página inicial.
                  </p>
                  <S.Button type="button" onClick={keepShopping}>
                    Ir para o início
                  </S.Button>
                </>
              )}

              {stage === "login" && (
                <>
                  <h2>Entre para ver seus ingressos</h2>
                  <p>Entre com seu telefone para ver seus ingressos.</p>
                  <S.Button type="button" onClick={() => navigate("/login")}>
                    Entrar com meu telefone
                  </S.Button>
                </>
              )}

              {stage === "not-found" && (
                <>
                  <h2>Link não encontrado</h2>
                  <p>
                    Não encontramos o evento deste link. Confira a mensagem
                    recebida ou acesse nossa página inicial.
                  </p>
                  <S.Button type="button" onClick={keepShopping}>
                    Ir para o início
                  </S.Button>
                </>
              )}
            </S.Block>
          )}
        </S.Main>
      </Container>

      <Footer />
    </S.Page>
  )
}

export default PublicTickets
